import { Button } from "@mui/material";

import { useAppSelector, useAppDispatch } from "../../store/hook";

import SettingsSection, { Cta } from "./Section";
import { UserLogin, UserRole } from "../../types/api/user";
import { useNavigate } from "react-router-dom";
import { setAuthUser } from "../../store/reducers/auth.slice";
import api from "../../utils/api/axios";
import { useEffect, useState } from "react";
import {
  StyledDialog,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
} from "../StyledDialog";
// import apiMercanet from "../../utils/mercanetApi/axios";

const formatSubscriptionDate = (date: string) =>
  new Intl.DateTimeFormat("fr-FR").format(new Date(date));

const getTodayIsoDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getIsoDatePart = (date?: string | null) => date?.split("T")[0];

const isPastDate = (date?: string | null) => {
  const isoDate = getIsoDatePart(date);
  if (!isoDate) return false;

  return isoDate < getTodayIsoDate();
};

const getGracePeriodEndDate = (date: string) => {
  const gracePeriodEndDate = new Date(date);
  gracePeriodEndDate.setDate(gracePeriodEndDate.getDate() + 2);

  return formatSubscriptionDate(gracePeriodEndDate.toISOString());
};

const formatPaymentMethod = (
  cardBrand?: string | null,
  cardDisplay?: string | null
) => {
  if (!cardBrand) return cardDisplay;
  if (!cardDisplay) return cardBrand;

  return cardBrand.toLowerCase() === cardDisplay.toLowerCase()
    ? cardBrand
    : `${cardBrand} ${cardDisplay}`;
};

const MySubscription = () => {
  const navigate = useNavigate();
  const { isAuth, user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [isManageRecurringDialogOpen, setIsManageRecurringDialogOpen] =
    useState(false);
  const [isCancelRecurringDialogOpen, setIsCancelRecurringDialogOpen] =
    useState(false);
  const [isEraseRecurringCardDialogOpen, setIsEraseRecurringCardDialogOpen] =
    useState(false);
  const [isCancelingRecurring, setIsCancelingRecurring] = useState(false);
  const [isErasingRecurringCard, setIsErasingRecurringCard] = useState(false);
  const [isReactivatingRecurring, setIsReactivatingRecurring] = useState(false);
  const getMe = async () => {
    try {
      const { data } = await api.get<UserLogin>("/auth/me");
      if (data) {
        dispatch(setAuthUser(data));
      }
    } catch (_) {}
  };

  useEffect(() => {
    if (isAuth) getMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  if (!user) return null;

  // const sendPaymentRequest = async () => {
  //   // send Mercanet payment request
  //   try {
  //     const { data: preapredDataForMercanet } = await api.post(
  //       "/subscription/init"
  //     );
  //     console.log(preapredDataForMercanet);
  //     // const mercanetReturn = await apiMercanet.post(
  //     //   // "/rs-services/v2/paymentInit",
  //     //   // "/paymentInit",
  //     //   "/rs-services/v2/paymentInit",
  //     //   preapredDataForMercanet
  //     // );
  //     // console.log(mercanetReturn);
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  const { allAccess, role } = user;
  const isRecurringMandateActive = user.recurringMandate?.status === "ACTIVE";
  const isRecurringMandateCanceled =
    user.recurringMandate?.status === "CANCELED";
  const hasExpiredSubscription = isPastDate(user.subscriptionExpiresAt);
  const canManageRecurring =
    !!user.recurringMandate && !hasExpiredSubscription;
  const canReactivateRecurring =
    isRecurringMandateCanceled && user.recurringMandate?.canReactivate === true;
  const canSubscribe =
    !allAccess || (user.isGracePeriod && !isRecurringMandateActive);
  const paymentMethod = formatPaymentMethod(
    user.recurringMandate?.cardBrand,
    user.recurringMandate?.cardDisplay
  );
  const gracePeriodMessage =
    user.isGracePeriod && user.subscriptionExpiresAt
      ? isRecurringMandateActive && user.recurringMandate?.nextRenewalAt
        ? `Votre abonnement est terminé depuis le ${formatSubscriptionDate(
            user.subscriptionExpiresAt
          )}. Son renouvellement automatique est prévu le ${formatSubscriptionDate(
            user.recurringMandate.nextRenewalAt
          )}`
        : `Votre abonnement est terminé depuis le ${formatSubscriptionDate(
            user.subscriptionExpiresAt
          )}, vous perdrez l'accès le ${getGracePeriodEndDate(
            user.subscriptionExpiresAt
          )}`
      : null;

  const isFreeWithAllAccesUser =
    allAccess &&
    [
      UserRole.ADMIN,
      UserRole.AUTHOR,
      UserRole.EDITOR,
      UserRole.MANAGER,
      UserRole.OLD_COLLABORATOR,
    ].includes(role);

  const ctaGetSubscription: Cta[] = [
    {
      children: "Activer mon abonnement pour 33,00€ TTC",
      onClick: () => navigate("/abonnement/facturation"),
      variant: "contained",
    },
  ];

  const cancelRecurring = async () => {
    setIsCancelingRecurring(true);
    try {
      await api.post("/subscription/recurring/cancel");
      await getMe();
      setIsCancelRecurringDialogOpen(false);
      setIsManageRecurringDialogOpen(false);
    } catch (error) {
      console.error("Error canceling recurring subscription", error);
    } finally {
      setIsCancelingRecurring(false);
    }
  };

  const eraseRecurringCard = async () => {
    setIsErasingRecurringCard(true);
    try {
      await api.post("/subscription/recurring/erase-card");
      await getMe();
      setIsEraseRecurringCardDialogOpen(false);
      setIsManageRecurringDialogOpen(false);
    } catch (error) {
      console.error("Error erasing recurring card", error);
    } finally {
      setIsErasingRecurringCard(false);
    }
  };

  const reactivateRecurring = async () => {
    setIsReactivatingRecurring(true);
    try {
      await api.post("/subscription/recurring/reactivate");
      await getMe();
    } catch (error) {
      console.error("Error reactivating recurring subscription", error);
    } finally {
      setIsReactivatingRecurring(false);
    }
  };

  return (
    <SettingsSection
      title="Mon abonnement"
      cta={canSubscribe ? ctaGetSubscription : undefined}
    >
      <p>
        {isFreeWithAllAccesUser
          ? "Vous avez un accès complet gratuit"
          : gracePeriodMessage
          ? gracePeriodMessage
          : allAccess
          ? "Votre abonnement est actif"
          : "Vous n'avez pas d'abonnement actif"}
      </p>

      {user.subscriptionExpiresAt ? (
        <p>
          Fin de validité de l'abonnement :{" "}
          {formatSubscriptionDate(user.subscriptionExpiresAt)}
        </p>
      ) : null}

      {canManageRecurring ? (
        <div>
          <Button
            variant="outlined"
            onClick={() => setIsManageRecurringDialogOpen(true)}
          >
            Gérer le renouvellement automatique
          </Button>
        </div>
      ) : null}

      {user.recurringMandate?.status === "CANCELED" ? (
        <p>
          Renouvellement automatique annulé
          {user.recurringMandate.nextRenewalAt
            ? `. Prochaine échéance conservée : ${formatSubscriptionDate(
                user.recurringMandate.nextRenewalAt
              )}`
            : ""}
        </p>
      ) : null}

      {user.recurringMandate?.status === "SUSPENDED" ? (
        <p>Renouvellement automatique suspendu</p>
      ) : null}

      <div>
        <Button variant="outlined" onClick={() => {}}>
          Recevoir la lettre d'information ePOPI
        </Button>
      </div>

      <StyledDialog
        open={canManageRecurring && isManageRecurringDialogOpen}
        onClose={() => setIsManageRecurringDialogOpen(false)}
        fullWidth
      >
        <StyledDialogTitle>
          Gérer le renouvellement automatique
        </StyledDialogTitle>

        <StyledDialogContent>
          <div>
            <p>
              Renouvellement automatique{" "}
              {isRecurringMandateActive ? "actif" : "annulé"}
            </p>
            {user.recurringMandate?.nextRenewalAt ? (
              <p>
                Prochaine échéance :{" "}
                {formatSubscriptionDate(user.recurringMandate.nextRenewalAt)}
              </p>
            ) : null}
            {paymentMethod ? (
              <p>Moyen de paiement : {paymentMethod}</p>
            ) : null}
          </div>
        </StyledDialogContent>

        <StyledDialogActions>
          <Button
            variant="outlined"
            onClick={() => setIsManageRecurringDialogOpen(false)}
            disabled={
              isCancelingRecurring ||
              isErasingRecurringCard ||
              isReactivatingRecurring
            }
          >
            Fermer
          </Button>
          {isRecurringMandateActive ? (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setIsCancelRecurringDialogOpen(true)}
                disabled={isCancelingRecurring || isErasingRecurringCard}
              >
                Stopper le renouvellement
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => setIsEraseRecurringCardDialogOpen(true)}
                disabled={isCancelingRecurring || isErasingRecurringCard}
              >
                Stopper et supprimer la carte
              </Button>
            </>
          ) : null}
          {canReactivateRecurring ? (
            <Button
              variant="contained"
              onClick={reactivateRecurring}
              disabled={isReactivatingRecurring}
            >
              Réactiver le renouvellement
            </Button>
          ) : null}
        </StyledDialogActions>
      </StyledDialog>

      <StyledDialog
        open={isCancelRecurringDialogOpen}
        onClose={() => setIsCancelRecurringDialogOpen(false)}
        fullWidth
      >
        <StyledDialogTitle>
          Annuler le renouvellement automatique
        </StyledDialogTitle>

        <StyledDialogContent>
          <p>
            Êtes-vous sûr de vouloir annuler le renouvellement automatique de
            votre abonnement ?
          </p>
        </StyledDialogContent>

        <StyledDialogActions>
          <Button
            variant="outlined"
            onClick={() => setIsCancelRecurringDialogOpen(false)}
            disabled={isCancelingRecurring}
          >
            Conserver
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={cancelRecurring}
            disabled={isCancelingRecurring}
          >
            Confirmer l'annulation
          </Button>
        </StyledDialogActions>
      </StyledDialog>

      <StyledDialog
        open={isEraseRecurringCardDialogOpen}
        onClose={() => setIsEraseRecurringCardDialogOpen(false)}
        fullWidth
      >
        <StyledDialogTitle>
          Supprimer les données de renouvellement
        </StyledDialogTitle>

        <StyledDialogContent>
          <p>
            Cette action annule le renouvellement automatique et supprime les
            données de carte utilisées pour le renouvellement. Elle n'est pas
            réversible.
          </p>
        </StyledDialogContent>

        <StyledDialogActions>
          <Button
            variant="outlined"
            onClick={() => setIsEraseRecurringCardDialogOpen(false)}
            disabled={isErasingRecurringCard}
          >
            Conserver
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={eraseRecurringCard}
            disabled={isErasingRecurringCard}
          >
            Supprimer définitivement
          </Button>
        </StyledDialogActions>
      </StyledDialog>
    </SettingsSection>
  );
};

export default MySubscription;
